import React, { useState } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Space, Select, Typography, Button } from 'antd';
import {
  UserOutlined,
  ShopOutlined,
  ShoppingOutlined,
  TeamOutlined,
  ReloadOutlined,
  RiseOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analyticsApi, dashboardApi, settlementApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState(7);

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
    refetchInterval: 30000,
  });

  const { data: recentOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: () => dashboardApi.getRecentOrders({ limit: 10 }),
  });

  const { data: salesData, refetch: refetchSales } = useQuery({
    queryKey: ['analytics-sales', timeRange],
    queryFn: () => analyticsApi.getSales(timeRange),
  });

  const { data: userGrowthData, refetch: refetchGrowth } = useQuery({
    queryKey: ['analytics-user-growth', timeRange],
    queryFn: () => analyticsApi.getUserGrowth(timeRange),
  });

  const { data: monthlyRevenueData, refetch: refetchMonthlyRevenue } = useQuery({
    queryKey: ['analytics-monthly-revenue'],
    queryFn: () => analyticsApi.getMonthlyRevenue(12),
  });

  const { data: productStatsData, refetch: refetchProducts } = useQuery({
    queryKey: ['analytics-products'],
    queryFn: () => analyticsApi.getProductStats(10),
  });

  const { data: settlementStatsData, refetch: refetchSettlement } = useQuery({
    queryKey: ['settlement-stats'],
    queryFn: async () => {
      try { return await settlementApi.getStats(); } catch { return {}; }
    },
  });

  const handleRefresh = () => {
    refetchStats();
    refetchSales();
    refetchGrowth();
    refetchMonthlyRevenue();
    refetchProducts();
    refetchSettlement();
  };

  const statsData = (stats as any) || {};
  const orders = (recentOrders as any) || [];
  const sales = (salesData as any) || [];
  const userGrowth = (userGrowthData as any) || [];
  const monthlyRevenue = (monthlyRevenueData as any) || [];
  const productStats = (productStatsData as any) || [];
  const settlementStats = (settlementStatsData as any) || {};

  // 销售趋势图（双轴：实际成交订单 + 实际收入）
  const salesChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: (params: any) => {
        let html = `${params[0]?.axisValue}<br/>`;
        params.forEach((p: any) => {
          const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${p.color};"></span>`;
          if (p.seriesName === '实际收入') {
            html += `${marker}${p.seriesName}：¥${Number(p.value).toFixed(2)}<br/>`;
          } else {
            html += `${marker}${p.seriesName}：${p.value} 笔<br/>`;
          }
        });
        return html;
      },
    },
    legend: { data: ['实际订单', '实际收入'], top: 0 },
    xAxis: {
      type: 'category',
      data: sales.map((item: any) => item.date.substring(5)),
      boundaryGap: false,
    },
    yAxis: [
      { type: 'value', name: '订单数', position: 'left' },
      {
        type: 'value', name: '收入 (¥)', position: 'right',
        axisLabel: { formatter: '¥{value}' },
      },
    ],
    series: [
      {
        name: '实际订单', type: 'line', yAxisIndex: 0, smooth: true,
        data: sales.map((item: any) => item.orders),
        itemStyle: { color: '#1890ff' },
      },
      {
        name: '实际收入', type: 'line', yAxisIndex: 1, smooth: true,
        data: sales.map((item: any) => item.revenue),
        itemStyle: { color: '#52c41a' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(82, 196, 26, 0.3)' },
              { offset: 1, color: 'rgba(82, 196, 26, 0.05)' },
            ],
          },
        },
      },
    ],
    grid: { left: 60, right: 80, top: 40, bottom: 40 },
  };

  // 用户增长图
  const userGrowthChartOption = {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: userGrowth.map((item: any) => item.date.substring(5)) },
    yAxis: { type: 'value', name: '新增用户' },
    series: [{
      name: '新增用户', type: 'bar', barWidth: '60%',
      data: userGrowth.map((item: any) => item.new_users),
      itemStyle: {
        color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: '#5B8FF9' }, { offset: 1, color: '#1890ff' }],
        },
      },
    }],
    grid: { left: 50, right: 20, top: 40, bottom: 40 },
  };

  const monthlyRevenueChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const point = params?.[0];
        if (!point) return '';
        return `${point.axisValue}<br/>月收入：¥${Number(point.value).toFixed(2)}`;
      },
    },
    xAxis: {
      type: 'category',
      data: monthlyRevenue.map((item: any) => item.month),
      boundaryGap: false,
      axisLabel: {
        formatter: (value: string) => value.slice(2),
      },
    },
    yAxis: {
      type: 'value',
      name: '收入 (¥)',
      axisLabel: {
        formatter: (value: number) => `¥${value}`,
      },
    },
    series: [{
      name: '月收入',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      data: monthlyRevenue.map((item: any) => item.revenue),
      itemStyle: { color: '#fa8c16' },
      lineStyle: { width: 3, color: '#fa8c16' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(250, 140, 22, 0.35)' },
            { offset: 1, color: 'rgba(250, 140, 22, 0.04)' },
          ],
        },
      },
    }],
    grid: { left: 60, right: 30, top: 40, bottom: 40 },
  };

  // 订单表格列
  const orderColumns = [
    {
      title: '订单号', dataIndex: 'order_number', key: 'order_number',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '用户', key: 'user',
      render: (_: any, record: any) => (
        <div>{record.username ? `@${record.username}` : record.first_name || '-'}</div>
      ),
    },
    { title: '商品', dataIndex: 'product_name', key: 'product_name' },
    {
      title: '金额', dataIndex: 'amount', key: 'amount',
      render: (amount: number, record: any) => {
        const isUSDT = record.payment_method === 'usdt';
        return <Text strong>{isUSDT ? `${amount.toFixed(2)} USDT` : `¥${amount.toFixed(2)}`}</Text>;
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status',
      render: (status: string) => {
        const m: any = {
          completed: { color: 'success', text: '已完成' },
          paid: { color: 'success', text: '已支付' },
          pending: { color: 'processing', text: '处理中' },
          failed: { color: 'error', text: '失败' },
          cancelled: { color: 'default', text: '已取消' },
        };
        const s = m[status] || { color: 'default', text: status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '创建时间', dataIndex: 'created_at', key: 'created_at',
      render: (date: string) => formatBeijingTime(date),
    },
  ];

  // 商品排行列
  const productColumns: ColumnsType<any> = [
    {
      title: '排名', key: 'rank', width: 60,
      render: (_: any, __: any, index: number) => {
        const colors = ['#FFD700', '#C0C0C0', '#CD7F32'];
        return index < 3
          ? <TrophyOutlined style={{ fontSize: 18, color: colors[index] }} />
          : <span className="text-gray-500">{index + 1}</span>;
      },
    },
    { title: '商品名称', dataIndex: 'product_name', key: 'product_name',
      render: (text: string) => <Text strong>{text}</Text> },
    { title: '销量', dataIndex: 'total_sales', key: 'total_sales', align: 'right' as const },
    {
      title: '总收入', dataIndex: 'total_revenue', key: 'total_revenue', align: 'right' as const,
      render: (v: number) => <Text strong className="text-green-600">¥{v.toFixed(2)}</Text>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* 标题栏 */}
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="mb-2">仪表板</Title>
          <Text type="secondary">业务数据概览</Text>
        </div>
        <Space>
          <Select value={timeRange} onChange={setTimeRange} style={{ width: 110 }}>
            <Option value={7}>近7天</Option>
            <Option value={30}>近30天</Option>
            <Option value={90}>近90天</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={statsLoading}>
            刷新
          </Button>
        </Space>
      </div>

      {/* 核心指标 */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} lg={3}>
          <Card size="small"><Statistic title="总用户" value={statsData.total_users || 0}
            prefix={<UserOutlined className="text-blue-500" />} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={3}>
          <Card size="small"><Statistic title="总商户" value={statsData.total_merchants || 0}
            prefix={<ShopOutlined className="text-cyan-500" />} valueStyle={{ color: '#13c2c2' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={3}>
          <Card size="small"><Statistic title="总订单" value={statsData.total_orders || 0}
            prefix={<ShoppingOutlined className="text-purple-500" />} valueStyle={{ color: '#722ed1' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small"><Statistic title="成交订单" value={statsData.completed_orders || 0}
            prefix={<ShoppingOutlined className="text-blue-500" />} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={3}>
          <Card size="small"><Statistic title="今日订单" value={statsData.today_orders || 0}
            prefix={<TeamOutlined className="text-orange-500" />} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small"><Statistic title="总收入" value={statsData.total_revenue || 0} precision={2}
            prefix="¥" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small"><Statistic title="今日收入" value={statsData.today_revenue || 0} precision={2}
            prefix="¥" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
      </Row>

      {/* 趋势图表 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={`近${timeRange}天实际成交趋势`} size="small">
            <ReactECharts option={salesChartOption} style={{ height: '320px' }} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={`近${timeRange}天用户增长`} size="small">
            <ReactECharts option={userGrowthChartOption} style={{ height: '320px' }} />
          </Card>
        </Col>
      </Row>

      <Card title="月度收入统计" size="small">
        <ReactECharts option={monthlyRevenueChartOption} style={{ height: '320px' }} />
      </Card>

      {/* 商品排行 + 结算统计 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="本月商品销售排行 TOP 10" size="small">
            <Table columns={productColumns} dataSource={productStats}
              pagination={false} rowKey="product_id" size="small" />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="结算数据" size="small">
            <Space direction="vertical" className="w-full" size="middle">
              <div className="flex justify-between"><Text>待处理提现</Text>
                <Text strong className="text-orange-600">{settlementStats.pending_count || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>待处理金额</Text>
                <Text strong className="text-orange-600">¥{(settlementStats.pending_amount || 0).toFixed(2)}</Text></div>
              <div className="flex justify-between"><Text>处理中</Text>
                <Text strong className="text-blue-600">{settlementStats.processing_count || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>已完成提现</Text>
                <Text strong className="text-green-600">{settlementStats.completed_count || 0} 笔</Text></div>
              <div className="flex justify-between"><Text>已支付金额</Text>
                <Text strong className="text-green-600">¥{(settlementStats.completed_amount || 0).toFixed(2)}</Text></div>
              <div className="flex justify-between"><Text>总提现金额</Text>
                <Text strong>¥{(settlementStats.total_amount || 0).toFixed(2)}</Text></div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 最近订单 */}
      <Card title="最近订单" size="small"
        extra={<Button type="link" onClick={() => navigate('/payments')}>查看全部</Button>}>
        <Table columns={orderColumns} dataSource={orders}
          pagination={false} size="small" rowKey="id" loading={ordersLoading} />
      </Card>
    </div>
  );
};

export default Dashboard;
